import { get, find, chain, first, last, indexOf, map, isEmpty, some } from "lodash"
import { createSelector } from "reselect"
import { getCurrency } from "modules/portfolio-root/selectors/portfolios"
import { getDates } from "modules/portfolio-root/selectors/dates"
import { percentageFormatter, makeCurrencyFormatter } from "helpers/formatters/numbers"
import { formatLongDate } from "helpers/formatters/dates"
import { getMostUsedOrDefaultModelId } from "helpers/models"
import { UPLOAD_CONFIG } from "components/upload/constants"
import { getModels } from "resources/models"

const EMPTY_ARRAY = []
const EMPTY_OBJECT = {}

export const getPortfolio = (state) => get(state, "global.data.portfolio.data")
export const getFiles = (state) => get(state, "modules.portfolioPositions.files")
export const getUploadedFiles = (state) => get(state, "modules.portfolioPositions.uploadedFiles")
const getRawScanResult = (state) => get(state, "modules.portfolioPositions.scanResult")
const getPortfolios = state => get(state, "global.data.portfolios.data", EMPTY_ARRAY)
export const getUploadOption = (state) => get(state, "modules.portfolioPositions.uploadOption")
export const getUploadDateTime = (state) => get(state, "modules.portfolioPositions.uploadDateTime")
export const getAccount = state => get(state, "global.selected.account")
export const getExperimentId = (state, props) => get(props, "params.experimentId")
export const getExperiment = (state) => get(state, "global.data.experiment.data.portfolio.experiment")
export const getUploadConfig = () => ({ ...UPLOAD_CONFIG })

export const getModelId = createSelector(
  [getPortfolio, getModels, getPortfolios],
  (portfolio, models, portfolios) => {
    if (!portfolio) {
      return null
    }

    const defaultModelId = get(portfolio, "modelId")
    if (defaultModelId && some(models, { id: defaultModelId })) {
      return defaultModelId
    }

    return getMostUsedOrDefaultModelId(portfolios, models)
  },
)

export const getPortfolioDetails = createSelector(
  [getPortfolio, getModels, getExperiment, getModelId],
  (portfolio, models, experiment, modelId) => {
    let name = portfolio.name
    let type = "portfolio"
    let id = portfolio.id
    const parent = {}
    const defaultModelId = portfolio.modelId
    const resolvedModel = find(models, { id: modelId })
    const usingFallbackModel = defaultModelId !== modelId

    if (experiment) {
      name = experiment.name
      id = experiment.id
      type = "experiment"
      parent.name = portfolio.name
      parent.id = portfolio.id
    }

    return ({
      id,
      name,
      type,
      parent,
      modelId,
      model: get(resolvedModel, "name"),
      defaultModelId,
      usingFallbackModel,
      live: portfolio.live,
    })
  },
)

export const getOverlappingDates = createSelector(
  [getDates, getRawScanResult],
  (dates, scanResult) => {
    const scanDates = get(scanResult, "scan.dates", EMPTY_ARRAY)
    return chain(scanDates)
      .map(scanDate => indexOf(dates, scanDate.date) >= 0 ? scanDate.date : null)
      .filter(date => date)
      .value()
  },
)

export const getScanResult = createSelector(
  [getRawScanResult, getCurrency],
  (scanResult, currency) => {
    if (scanResult) {
      const scanSummary = get(scanResult, "scan.summary", EMPTY_OBJECT)
      const scanDates = get(scanResult, "scan.dates", EMPTY_ARRAY)

      return {
        ...scanResult,
        summary: {
          items: {
            averageEquity: {
              label: "Average Equity",
              value: makeCurrencyFormatter(currency)(scanSummary.averageEquity || scanSummary.averageGmv),
            },
            modelCoverage: {
              label: "Model Coverage",
              value: percentageFormatter(scanSummary.averageCoverage / 100),
            },
            numberOfDates: {
              label: "Dates found",
              value: scanDates.length,
            },
            averageSecurities: {
              label: "Average Number of Securities",
              value: scanSummary.averageSecurities,
            },
            // Only add the key if there is information on tag coverage
            ...(!isEmpty(scanSummary.averageTagCoverage) && {
              averageTagCoverage: {
                label: "Average Tag Coverage",
                value: map(scanSummary.averageTagCoverage, (value, tagId) => ({
                  label: tagId,
                  value: percentageFormatter(value / 100),
                })),
              },
            }),
          },
          from: formatLongDate(get(first(scanDates), "date")),
          to: formatLongDate(get(last(scanDates), "date")),
        },
      }
    }

    return null
  },
)
